<template>
    <GlobalLoader />
    <div class="card shadow-sm border-0">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="true"
                :btnTitle="'Insert'"
                @onClickAddNewButton="insertIntoTable"
                @onClickCloseButton="onClickCloseButton"
                title="Upload Employees"
            />
        </div>

        <div class="p-3">
            <template v-for="(item, key) in csvData" :key="key">
                <div v-if="!uploadedBatches.includes(item.key)" :class="key != 0 ? 'mt-4' : ''">
                    <div class="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom">
                        <span class="badge bg-primary rounded-pill px-3 py-1 fs-6">Batch {{ item.key }}</span>
                        <div v-if="item.key === uploadingBatch" class="spinner-border spinner-border-sm text-success" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div class="px-1">
                        <div v-for="(child, index) in item.data" :key="index" class="mb-2 rounded border" :class="child.status === 'error' ? 'border-danger' : 'border-light'">
                            <div class="d-flex align-items-center justify-content-between px-3 py-2" :class="child.status === 'error' ? 'bg-danger bg-opacity-10' : 'bg-light'">
                                <div class="d-flex align-items-center gap-2">
                                    <i class="fas fa-user-circle text-secondary fs-5"></i>
                                    <span class="fw-semibold" :class="child.status === 'error' ? 'text-danger' : 'text-dark'">
                                        {{ getTitle(child.data) }}
                                    </span>
                                    <span v-if="child.status === 'error'" class="badge bg-danger bg-opacity-75 small ms-1">
                                        {{ getErrors(child.validation) }}
                                    </span>
                                </div>
                                <button
                                    class="btn btn-sm d-flex align-items-center gap-1"
                                    :class="editingKey === `${item.key}-${index}` ? 'btn-secondary' : 'btn-primary'"
                                    @click="toggleEdit(item.key, index, child)"
                                >
                                    <i :class="editingKey === `${item.key}-${index}` ? 'fas fa-times' : 'fas fa-pencil-alt'"></i>
                                    <span>{{ editingKey === `${item.key}-${index}` ? 'Close' : 'Edit' }}</span>
                                </button>
                            </div>
                            <div v-if="editingKey === `${item.key}-${index}`" class="px-3 py-2 bg-white">
                                <div class="row g-1">
                                    <div v-for="field in editableFields" :key="field.key" class="col-12 col-sm-6 col-md-3">
                                        <label class="form-label mb-0 fw-bold" style="font-size:0.8rem;color:#212529">{{ field.label }}</label>
                                        <template v-if="field.type === 'select'">
                                            <select v-model="editForm[field.key]" class="form-select form-select-sm">
                                                <option value="">Select {{ field.label }}</option>
                                                <option v-for="(label, key) in dropdownOptions[field.optionsKey]" :key="key" :value="key">{{ label }}</option>
                                            </select>
                                        </template>
                                        <template v-else-if="field.type === 'vselect'">
                                            <v-select
                                                :placeholder="`Select ${field.label}`"
                                                v-model="editForm[field.key]"
                                                :options="dropdownOptions[field.optionsKey]"
                                                label="name"
                                                :reduce="opt => opt.id"
                                                class="form-control-sm p-0"
                                            />
                                        </template>
                                        <template v-else-if="field.type === 'asyncselect'">
                                            <AsyncSelect
                                                :placeholder="`Select ${field.label}`"
                                                v-model="editForm[field.nameKey]"
                                                :api-service="fetchContactProfiles"
                                                :additional-query="{ ...field.query, company_id: route.params.companyId }"
                                                label="name"
                                                :reduce="opt => opt.name"
                                            />
                                        </template>
                                        <template v-else>
                                            <input
                                                v-model="editForm[field.key]"
                                                :type="field.type || 'text'"
                                                class="form-control form-control-sm"
                                                :placeholder="field.label"
                                            />
                                        </template>
                                    </div>
                                </div>
                                <div class="mt-2 d-flex gap-2">
                                    <button
                                        class="btn btn-sm btn-primary px-3"
                                        :disabled="saving"
                                        @click="saveRow(item, index)"
                                    >
                                        <i class="fas fa-save me-1"></i>Save
                                    </button>
                                    <button class="btn btn-sm btn-outline-secondary px-3" @click="cancelEdit">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import TitleButton from '@/components/atom/TitleButton.vue';
import GlobalLoader from '@/components/atom/GlobalLoader.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import handleInventory from '@/services/modules/inventory';
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll';
import handleChartAccount from '@/services/modules/chartAccount';
import handleDropdowns from '@/services/modules/dropdowns';
import handleDesignation from '@/services/modules/procurement/organization-settings/designation';
import handleHospital from '@/services/modules/hospital';
import handleContact from '@/services/modules/contact';

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchCsvData } = handleInventory();
const { insertEmployees, updateEmployeeRow } = handleHrPayroll();
const { fetchHome } = handleChartAccount();
const { fetchDropdownList } = handleDropdowns();
const { fetchDesignationList } = handleDesignation();
const { fetchNoteMasterList } = handleHospital();
const { fetchContactProfiles } = handleContact();

const genders = ref({});
const bloodGroups = ref({});
const maritalStatus = ref({});
const religions = ref({});
const branchList = ref([]);
const divisionList = ref([]);
const departmentList = ref([]);
const businessList = ref([]);
const designationList = ref([]);
const employeeCategoryList = ref([]);
const jobStatusList = ref([]);

const dropdownOptions = {
    get genders() { return genders.value; },
    get bloodGroups() { return bloodGroups.value; },
    get maritalStatus() { return maritalStatus.value; },
    get religions() { return religions.value; },
    get branchList() { return branchList.value; },
    get divisionList() { return divisionList.value; },
    get departmentList() { return departmentList.value; },
    get businessList() { return businessList.value; },
    get designationList() { return designationList.value; },
    get employeeCategoryList() { return employeeCategoryList.value; },
    get jobStatusList() { return jobStatusList.value; },
};

const csvData = ref([]);
const uploadingBatch = ref('');
const uploadedBatches = ref([]);

const editingKey = ref(null);
const editForm = ref({});
const saving = ref(false);

const editableFields = [
    { key: 'staff_id', label: 'Staff ID' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'birthday', label: 'Birthday', type: 'date' },
    { key: 'gender', label: 'Gender', type: 'select', optionsKey: 'genders' },
    { key: 'blood_group', label: 'Blood Group', type: 'select', optionsKey: 'bloodGroups' },
    { key: 'marital_status', label: 'Marital Status', type: 'select', optionsKey: 'maritalStatus' },
    { key: 'religion', label: 'Religion', type: 'select', optionsKey: 'religions' },
    { key: 'nid', label: 'NID' },
    { key: 'tin_no', label: 'TIN No' },
    { key: 'father_name', label: 'Father Name' },
    { key: 'mother_name', label: 'Mother Name' },
    { key: 'spouse_name', label: 'Spouse Name' },
    { key: 'mobile_no', label: 'Mobile No' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'full_address', label: 'Full Address' },
    { key: 'location', label: 'Location' },
    { key: 'employment_branch_id', label: 'Branch', type: 'vselect', optionsKey: 'branchList', nameKey: 'employment_branch_name' },
    { key: 'employment_division_id', label: 'Division', type: 'vselect', optionsKey: 'divisionList', nameKey: 'employment_division_name' },
    { key: 'employment_department_id', label: 'Department', type: 'vselect', optionsKey: 'departmentList', nameKey: 'employment_department_name' },
    { key: 'employment_business_unit_id', label: 'Business Unit', type: 'vselect', optionsKey: 'businessList', nameKey: 'employment_business_unit_name' },
    { key: 'employment_date_of_joining', label: 'Date of Joining', type: 'date' },
    { key: 'employment_confirmation_date', label: 'Confirmation Date', type: 'date' },
    { key: 'employment_designation_id', label: 'Designation', type: 'vselect', optionsKey: 'designationList', nameKey: 'employment_designation_name' },
    { key: 'employment_designation_effective_date', label: 'Designation Effective Date', type: 'date' },
    { key: 'employment_employee_category_id', label: 'Employee Category', type: 'vselect', optionsKey: 'employeeCategoryList', nameKey: 'employment_employee_category_name' },
    { key: 'employment_job_status_id', label: 'Job Status', type: 'vselect', optionsKey: 'jobStatusList', nameKey: 'employment_job_status_name' },
    { key: 'leave_approver_id', label: 'Leave Approver', type: 'asyncselect', query: { type: 'employee' }, nameKey: 'leave_approver' },
    { key: 'account_name', label: 'Account Name' },
    { key: 'account_no', label: 'Account No' },
    { key: 'bank_id', label: 'Bank Name', type: 'asyncselect', query: { roles: 'bank' }, nameKey: 'bank_name' },
    { key: 'bank_branch_name', label: 'Bank Branch' },
    { key: 'routing_no', label: 'Routing No' },
];

const loadDropdowns = async () => {
    const companyQuery = `?company_id=${route.params.companyId}`;

    fetchHome().then(res => {
        if (!res.data) return;
        genders.value = res.data.genders;
        bloodGroups.value = res.data.blood_groups;
        maritalStatus.value = res.data.marital_status;
        religions.value = res.data.religions;
    });

    fetchDropdownList(companyQuery + '&is_branch=1&is_division=1&is_department=1&is_business=1').then(res => {
        if (!res.status) return;
        branchList.value = res.data.branch_list;
        divisionList.value = res.data.division_list;
        departmentList.value = res.data.department_list;
        businessList.value = res.data.business_list;
    });

    fetchDesignationList(companyQuery).then(res => {
        if (res.status) designationList.value = res.data;
    });

    fetchNoteMasterList(companyQuery + '&is_all=true&status=active&note_type=employee_category').then(res => {
        if (res.status) employeeCategoryList.value = res.data;
    });

    fetchNoteMasterList(companyQuery + '&is_all=true&status=active&note_type=job_status').then(res => {
        if (res.status) jobStatusList.value = res.data;
    });

};

onMounted(() => {
    const query = `?id=${route.query.rowId}&company_id=${route.params.companyId}`;
    fetchCsvData(query).then((res) => {
        if (!res.status) return;
        csvData.value = Object.keys(res.data).map((key) => ({
            key,
            data: res.data[key],
        }));
    });
    loadDropdowns();
});

const onClickCloseButton = () => {
  router.push({ name: 'employee-import', params: route.params, query: route.query });
};

const insertIntoTable = () => {
    if (store.state.loading) return;
    store.state.loading = true;
    uploadBatch(0);
};

const getTitle = (data) => {
    let title = data.full_name ?? '';
    if (data.staff_id) title += ` (${data.staff_id})`;
    if (data.mobile_no) title += ` - ${data.mobile_no}`;
    if (data.email) title += ` - ${data.email}`;
    return title;
};

const getErrors = (validation) => {
    if (!validation) return '';
    return Object.values(validation).flat().join(', ');
};

const vselectKeys = new Set(editableFields.filter(f => f.type === 'vselect').map(f => f.key));

const toggleEdit = (batchKey, index, child) => {
    const key = `${batchKey}-${index}`;
    if (editingKey.value === key) {
        cancelEdit();
        return;
    }
    editingKey.value = key;
    const data = { ...child.data };
    for (const k of vselectKeys) {
        const v = Number(data[k]);
        data[k] = (v > 0) ? v : null;
    }
    editForm.value = data;
};

const cancelEdit = () => {
    editingKey.value = null;
    editForm.value = {};
};

const getAbsoluteRowIndex = (batchKey, localIndex) => {
    let offset = 0;
    for (const item of csvData.value) {
        if (item.key === batchKey) return offset + localIndex;
        offset += item.data.length;
    }
    return -1;
};

const syncVselectNames = (form) => {
    const vselectFields = editableFields.filter(f => f.type === 'vselect');
    for (const field of vselectFields) {
        const id = form[field.key];
        if (!id || !field.nameKey) continue;
        const options = dropdownOptions[field.optionsKey];
        if (!Array.isArray(options)) continue;
        const found = options.find(o => String(o.id) === String(id));
        if (found) form[field.nameKey] = found.name;
    }
};

const saveRow = async (item, index) => {
    saving.value = true;
    const rowIndex = getAbsoluteRowIndex(item.key, index);
    const formCopy = { ...editForm.value };
    syncVselectNames(formCopy);

    const payload = {
        company_id: route.params.companyId,
        id: route.query.rowId,
        row_index: rowIndex,
        data: formCopy,
    };

    await updateEmployeeRow(payload).then((res) => {
        if (!res.status) return showError(res.message);
        item.data[index].data = { ...formCopy };
        item.data[index].status = 'pending';
        item.data[index].validation = [];
        showSuccess(res.message);
        cancelEdit();
    }).catch(() => {
        showError('Something went wrong');
    });

    saving.value = false;
};

const uploadBatch = async (index) => {
    if (index === csvData.value.length) {
        store.state.loading = false;
        router.back();
        return;
    }

    const formData = {
        company_id: route.params.companyId,
        id: route.query.rowId,
        batch: csvData.value[index].key,
    };

    uploadingBatch.value = csvData.value[index].key;

    await insertEmployees(formData).then((res) => {
        if (!res.status) {
            store.state.loading = false;
            return;
        }
        uploadedBatches.value.push(uploadingBatch.value);
        uploadingBatch.value = '';
        setTimeout(() => {
            uploadBatch(index + 1);
        }, 5000);
    });
};
</script>
